import React, { useState } from 'react';

import { View } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { CardModal } from 'components/card-modal';
import useStoreLocatorPerformanceEvents from 'pages/store-locator/hooks/use-store-locator-performance-events';

import { ListView, MapView, SearchBar, StoreChangeDisclaimer } from './components';
import { ClearStoreSmButton } from './components/clear-store-sm-button';
import { ToggleViewButton } from './components/toggle-view-button';
import { IStoreLocatorViewProps } from './components/types';
import {
  MaxWidthFullHeight,
  SearchContainer,
  ServiceModeCategoryToggleWrapper,
  ToggleButton,
  ToggleButtonLabel,
  ToggleButtonsWrapper,
} from './store-locator.styled';
import { StoreLocatorViews } from './types';

export const StoreLocatorView: React.FC<IStoreLocatorViewProps> = ({
  activeCoordinates,
  errorFavs,
  errorNearby,
  errorRecent,
  handleResetLocation,
  searchingNearby,
  searchingRecent,
  searchingFavs,
  rawIsSearching,
  storesFavs = [],
  storesNearby = [],
  storesRecent = [],
  handleSelectStore,
  onDismissInSupportForm,

  // Map View
  onPressSearch,

  // View
  currentView,
  onViewChange,
  isInSupportForm,

  // Search Bar
  onAddressSelected,
  onSearchBarValueChange,
  searchBarValue,
  shouldFocusSearchBar,

  // Service Mode Category
  hideServiceModeCategories = false,
  onServiceModeCategoryChange,
  selectedServiceModeCategory,
  serviceModeCategoryOptions = [],
}) => {
  useStoreLocatorPerformanceEvents(storesNearby, 'default');
  const { formatMessage } = useIntl();
  const [restaurantFocused, setRestaurantFocused] = useState<IRestaurant | undefined>();

  // The experiment has a header that fits in the support form, so we need to render it there.
  // when the experiment is not enabled, from the support locator form we need to render a different header
  const isInSupportFormAndEnableMapListExperiment = isInSupportForm;

  return (
    <CardModal
      testID="store-locator-wrapper"
      headerText={formatMessage({ id: 'selectLocation' })}
      invertHeaderColor
      alwaysHideHeader={isInSupportForm || isInSupportFormAndEnableMapListExperiment}
      customHeaderLeft={
        <ToggleViewButton currentView={currentView} updateStoreLocatorView={onViewChange} />
      }
    >
      <ClearStoreSmButton />
      <SearchContainer>
        {!isInSupportForm && !hideServiceModeCategories ? (
          <ServiceModeCategoryToggleWrapper>
            <ToggleButtonsWrapper testID="service-mode-category-toggle">
              {serviceModeCategoryOptions.map(category => {
                const isSelected = selectedServiceModeCategory === category.value;
                const accessibilityLabel = `${category.label}${
                  isSelected ? formatMessage({ id: 'selected' }) : ''
                }`;

                return (
                  <ToggleButton
                    key={category.label}
                    onPress={() => onServiceModeCategoryChange(category.value)}
                    testID={`service-mode-${category.value}`}
                    selected={isSelected}
                    accessibilityRole="button"
                  >
                    <ToggleButtonLabel
                      accessibilityLabel={accessibilityLabel}
                      selected={isSelected}
                    >
                      {category.label}
                    </ToggleButtonLabel>
                  </ToggleButton>
                );
              })}
            </ToggleButtonsWrapper>
          </ServiceModeCategoryToggleWrapper>
        ) : null}
      </SearchContainer>
      <View
        style={{ zIndex: Styles.zIndex.top, marginTop: spacing.$4, marginHorizontal: spacing.$4 }}
      >
        <SearchBar
          // @ts-expect-error TS(2322) FIXME: Type '{ autoFocus: boolean; value: string; onInput... Remove this comment to see the full error message
          autoFocus={shouldFocusSearchBar}
          value={searchBarValue}
          onInputValueChange={onSearchBarValueChange}
          onAddressSelected={onAddressSelected}
        />
      </View>

      <StoreChangeDisclaimer marginX="$4" marginTop="$2" marginBottom="$4" />

      {currentView === StoreLocatorViews.LIST_VIEW ? (
        <ListView
          errorFavs={errorFavs}
          errorNearby={errorNearby}
          errorRecent={errorRecent}
          searchingNearby={searchingNearby}
          searchingRecent={searchingRecent}
          searchingFavs={searchingFavs}
          rawIsSearching={rawIsSearching}
          storesFavs={storesFavs}
          storesNearby={storesNearby}
          storesRecent={storesRecent}
          handleSelectStore={handleSelectStore}
          isInSupportForm={isInSupportForm}
          restaurantFocused={restaurantFocused}
          setRestaurantFocused={setRestaurantFocused}
        />
      ) : null}

      <MaxWidthFullHeight display={currentView === StoreLocatorViews.MAP_VIEW ? 'flex' : 'none'}>
        <MapView
          activeCoordinates={activeCoordinates}
          currentViewIsMap={currentView === StoreLocatorViews.MAP_VIEW}
          handleResetLocation={handleResetLocation}
          onPressSearch={onPressSearch}
          storesFavs={storesFavs}
          storesNearby={storesNearby}
          storesRecent={storesRecent}
          handleSelectStore={handleSelectStore}
          isInSupportForm={isInSupportForm}
        />
      </MaxWidthFullHeight>
    </CardModal>
  );
};
